import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, CheckSquare, MessageSquare, LogOut,
  CheckCircle, Clock, AlertCircle, Send, RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import './PortalPage.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function portalApi(method, path, data, config = {}) {
  const token = localStorage.getItem('portalToken');
  return axios({
    method,
    url: `${API_URL}/api/portal${path}`,
    data,
    ...config,
    headers: { Authorization: `Bearer ${token}`, ...config.headers },
  });
}

const STATUS_CONFIG = {
  open:    { label: 'Open',     color: '#f59e0b', bg: '#fffbeb', icon: Clock },
  partial: { label: 'Partial',  color: '#3b82f6', bg: '#eff6ff', icon: RefreshCw },
  closed:  { label: 'Complete', color: '#10b981', bg: '#ecfdf5', icon: CheckCircle },
};

export default function PortalPage() {
  const navigate = useNavigate();
  const sessionStr = localStorage.getItem('portalSession');
  const session = sessionStr ? JSON.parse(sessionStr) : null;

  const [activeTab, setActiveTab] = useState('upload');
  const [deal, setDeal] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Upload state
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState('');
  const fileInputRef = useRef();

  // Message state
  const [msgContent, setMsgContent] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const msgEndRef = useRef();
  const msgPollRef = useRef();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) { navigate('/portal/accept'); return; }
    loadAll();
  }, []);

  useEffect(() => {
    if (activeTab === 'messages') {
      scrollToBottom();
      // Poll for new messages every 5s while on messages tab
      msgPollRef.current = setInterval(() => loadMessages(), 5000);
    }
    return () => clearInterval(msgPollRef.current);
  }, [activeTab]);

  const scrollToBottom = () => setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

  const loadAll = async () => {
    try {
      const [dealRes, docsRes, reqRes, msgRes] = await Promise.all([
        portalApi('get', '/deal'),
        portalApi('get', '/documents'),
        portalApi('get', '/requests'),
        portalApi('get', '/messages'),
      ]);
      setDeal(dealRes.data);
      setDocuments(docsRes.data || []);
      setRequests(reqRes.data || []);
      setMessages(msgRes.data || []);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/portal/accept'); return; }
      setError('Failed to load deal data.');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const { data } = await portalApi('get', '/messages');
      setMessages(data || []);
    } catch {}
  };

  const handleSignOut = () => {
    localStorage.removeItem('portalToken');
    localStorage.removeItem('portalSession');
    navigate('/portal/login');
  };

  // ── Upload ────────────────────────────────────────────────
  const uploadFile = async (file, requestId) => {
    setUploading(true);
    setUploadError('');
    setUploadSuccess('');
    const formData = new FormData();
    formData.append('file', file);
    if (requestId) formData.append('requestId', requestId);
    try {
      await portalApi('post', '/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadSuccess(`"${file.name}" uploaded successfully.`);
      setSelectedRequestId('');
      // Refresh docs + requests
      const [docsRes, reqRes] = await Promise.all([
        portalApi('get', '/documents'),
        portalApi('get', '/requests'),
      ]);
      setDocuments(docsRes.data || []);
      setRequests(reqRes.data || []);
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file, selectedRequestId);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file, selectedRequestId);
  };

  // ── Requests ──────────────────────────────────────────────
  const markRequestClosed = async (requestId) => {
    try {
      await portalApi('patch', `/requests/${requestId}`, { status: 'closed' });
      setRequests(prev => prev.map(r => r.id === requestId ? { ...r, status: 'closed' } : r));
    } catch {}
  };

  // ── Messages ─────────────────────────────────────────────
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msgContent.trim() || sendingMsg) return;
    setSendingMsg(true);
    try {
      const { data } = await portalApi('post', '/messages', { content: msgContent.trim() });
      setMessages(prev => [...prev, data]);
      setMsgContent('');
      scrollToBottom();
    } catch {}
    finally { setSendingMsg(false); }
  };

  if (loading) {
    return (
      <div className="portal-loading">
        <div className="portal-spinner" />
        <p>Loading your portal…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-loading">
        <AlertCircle size={32} color="#ef4444" />
        <p style={{ color: '#ef4444' }}>{error}</p>
      </div>
    );
  }

  const openRequests = requests.filter(r => r.status !== 'closed');
  const tabs = [
    { id: 'upload',   label: 'Upload',   icon: Upload,        badge: null },
    { id: 'requests', label: 'Requests', icon: CheckSquare,   badge: openRequests.length || null },
    { id: 'files',    label: 'My Files', icon: FileText,      badge: documents.length || null },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: null },
  ];

  return (
    <div className="portal-root">
      {/* Header */}
      <header className="portal-header">
        <div className="portal-header-left">
          <div className="portal-logo">DN</div>
          <div>
            <div className="portal-deal-name">{deal?.name}</div>
            <div className="portal-deal-meta">
              {deal?.buyerOrg} · <span style={{ textTransform: 'capitalize' }}>{session?.role}</span> Portal
            </div>
          </div>
        </div>
        <div className="portal-header-right">
          <span className="portal-user-name">{session?.displayName}</span>
          <button className="portal-signout-btn" onClick={handleSignOut} title="Sign out">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="portal-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`portal-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <tab.icon size={16} />
            <span>{tab.label}</span>
            {tab.badge ? <span className="portal-tab-badge">{tab.badge}</span> : null}
          </button>
        ))}
      </nav>

      {/* Content */}
      <main className="portal-content">

        {/* ── Upload tab ── */}
        {activeTab === 'upload' && (
          <div className="portal-section">
            <h2 className="portal-section-title">Upload Documents</h2>
            <p className="portal-section-desc">
              Files you upload go directly to the deal team for review.
            </p>

            {openRequests.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label className="portal-label">Link to a request (optional)</label>
                <select
                  className="portal-select"
                  value={selectedRequestId}
                  onChange={e => setSelectedRequestId(e.target.value)}
                >
                  <option value="">— Not linked to a specific request —</option>
                  {openRequests.map(r => (
                    <option key={r.id} value={r.id}>{r.title}</option>
                  ))}
                </select>
              </div>
            )}

            <div
              className={`portal-dropzone ${dragging ? 'dragging' : ''} ${uploading ? 'disabled' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileInput} />
              {uploading ? (
                <>
                  <div className="portal-spinner" style={{ margin: '0 auto 0.75rem' }} />
                  <p>Uploading…</p>
                </>
              ) : (
                <>
                  <Upload size={32} color="#9ca3af" style={{ marginBottom: '0.75rem' }} />
                  <p style={{ fontWeight: 600, color: '#374151', margin: '0 0 0.25rem' }}>
                    Drop a file here or click to browse
                  </p>
                  <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>
                    PDF, Word, Excel, images — up to 50MB
                  </p>
                </>
              )}
            </div>

            {uploadSuccess && (
              <div className="portal-alert success">
                <CheckCircle size={16} /> {uploadSuccess}
              </div>
            )}
            {uploadError && (
              <div className="portal-alert error">
                <AlertCircle size={16} /> {uploadError}
              </div>
            )}
          </div>
        )}

        {/* ── Requests tab ── */}
        {activeTab === 'requests' && (
          <div className="portal-section">
            <h2 className="portal-section-title">Document Requests</h2>
            <p className="portal-section-desc">
              The deal team has requested the following documents. Upload files to fulfil them.
            </p>
            {requests.length === 0 ? (
              <div className="portal-empty">
                <CheckCircle size={32} color="#10b981" />
                <p>No document requests yet.</p>
              </div>
            ) : (
              <div className="portal-request-list">
                {requests.map(req => {
                  const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.open;
                  const StatusIcon = cfg.icon;
                  return (
                    <div key={req.id} className="portal-request-card">
                      <div className="portal-request-header">
                        <div className="portal-request-title">{req.title}</div>
                        <span className="portal-request-status" style={{ color: cfg.color, background: cfg.bg }}>
                          <StatusIcon size={13} /> {cfg.label}
                        </span>
                      </div>
                      {req.description && (
                        <p className="portal-request-desc">{req.description}</p>
                      )}
                      <div className="portal-request-footer">
                        {req.priority && (
                          <span className="portal-request-priority" data-priority={req.priority}>
                            {req.priority.charAt(0).toUpperCase() + req.priority.slice(1)} priority
                          </span>
                        )}
                        {req.due_date && (
                          <span className="portal-request-due">
                            Due {new Date(req.due_date).toLocaleDateString()}
                          </span>
                        )}
                        {req.status !== 'closed' && (
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                            <button
                              className="portal-btn-sm"
                              onClick={() => { setSelectedRequestId(req.id); setActiveTab('upload'); }}
                            >
                              <Upload size={13} /> Upload for this
                            </button>
                            <button
                              className="portal-btn-sm secondary"
                              onClick={() => markRequestClosed(req.id)}
                            >
                              <CheckCircle size={13} /> Mark complete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── My Files tab ── */}
        {activeTab === 'files' && (
          <div className="portal-section">
            <h2 className="portal-section-title">My Uploaded Files</h2>
            <p className="portal-section-desc">Latest versions of documents you've submitted.</p>
            {documents.length === 0 ? (
              <div className="portal-empty">
                <FileText size={32} color="#9ca3af" />
                <p>No files uploaded yet.</p>
              </div>
            ) : (
              <div className="portal-file-list">
                {documents.map(doc => (
                  <div key={doc.id} className="portal-file-card">
                    <FileText size={20} color="#6b7280" style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="portal-file-name">{doc.filename}</div>
                      <div className="portal-file-meta">
                        v{doc.version} · {doc.category || 'General'} ·{' '}
                        {new Date(doc.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <span className="portal-file-status" data-status={doc.status}>{doc.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Messages tab ── */}
        {activeTab === 'messages' && (
          <div className="portal-messages-container">
            <div className="portal-messages-list">
              {messages.length === 0 ? (
                <div className="portal-empty" style={{ margin: 'auto' }}>
                  <MessageSquare size={32} color="#9ca3af" />
                  <p>No messages yet. Say hello!</p>
                </div>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.id}
                    className={`portal-message ${msg.sender_type === 'portal' ? 'mine' : 'theirs'}`}
                  >
                    <div className="portal-message-name">{msg.sender_name}</div>
                    <div className="portal-message-bubble">{msg.content}</div>
                    <div className="portal-message-time">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))
              )}
              <div ref={msgEndRef} />
            </div>
            <form className="portal-message-form" onSubmit={sendMessage}>
              <input
                className="portal-message-input"
                type="text"
                placeholder="Type a message…"
                value={msgContent}
                onChange={e => setMsgContent(e.target.value)}
                maxLength={4000}
              />
              <button
                type="submit"
                className="portal-message-send"
                disabled={!msgContent.trim() || sendingMsg}
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        )}

      </main>
    </div>
  );
}
